import { defineStore } from 'pinia';
import { useRouter } from "vue-router";
import { ref } from 'vue';
import axios from "axios";
import { useToastStore } from "@/stores/toastStore.js";

let logoutInProgress  = null

export const useUserStore = defineStore('user-store',  () => {
  const toastStore = useToastStore()
  const router = useRouter()

  const userId = ref(null)
  const isAdmin = ref(false)

  function updateUserState(userData) {
    userId.value = userData.user_id
    isAdmin.value = userData.admin || false
  }

  function clearUserState() {
    userId.value = null
    isAdmin.value = false
  }

  async function login(email, password) {
    try {
      const response = await axios.post('/api/authentication/login', { email, password })
      updateUserState(response.data)
      toastStore.showToastMessage('Login successful', 'success', 'add')

      // Redirect to intended destination or home
      const redirectPath = router.currentRoute.value.query.redirect || '/'
      router.push(redirectPath)
    }
    catch (error) {
      const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Invalid email or password.'
      toastStore.showToastMessage(errorMessage, 'error', 'close')
    }
  }

  async function fetchCurrentUser() {
    // validateStatus is used because axios will throw an error for 4xx and 5xx responses - this causes any function calling fetchUser to receive an error
    // here we set it to always be validated to true
    const response = await axios.get('/api/authentication/current_user', { validateStatus: () => true })
    updateUserState(response.data)
  }

  async function logout() {
    // logoutInProgress prevents multiple 401 calls at once from all triggering a logout
    if (!logoutInProgress) {
      logoutInProgress  = (async () => {
        try {
          await axios.post('/api/authentication/logout')
          toastStore.showToastMessage('Logged out successfully', 'success', 'add')
        }
        catch (error) {
          console.error('Error logging out:', error)
          const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Error logging out'
          toastStore.showToastMessage(errorMessage, 'error', 'close')
        }
        finally {
          clearUserState()
          router.push({ name: 'login' })
          logoutInProgress  = null
        }
      })()
    }
  }

  async function registerUser(userData, token, passwordConfirmation) {
    try {
      const response = await axios.post('/api/authentication/register', {
        token,
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        password: userData.password,
        password_confirmation: passwordConfirmation
      })
      updateUserState(response.data)
      toastStore.showToastMessage('Registration successful', 'success', 'add')
      router.push('/') // Route user to home screen after successfully registering
    } catch (error) {
      console.error('Error registering user:', error)
      const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Registration failed. Please try again.'
      toastStore.showToastMessage(errorMessage, 'error', 'close')
    }
  }


  async function forgotPassword(email) {
    try {
      await axios.post('/api/authentication/forgot_password', { email })
      toastStore.showToastMessage('Password reset instructions have been sent', 'success', 'add')
      router.push({ name: 'login' })
    } catch (error) {
      console.error('Error sending password reset:', error)
      const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Failed to send password reset instructions. Please try again.'
      toastStore.showToastMessage(errorMessage, 'error', 'close')
    }
  }

  async function resetPassword(token, password, passwordConfirmation) {
    try {
      await axios.post('/api/authentication/reset_password', {
        reset_password_token: token,
        password,
        password_confirmation: passwordConfirmation
      })
      toastStore.showToastMessage('Password reset successfully', 'success', 'add')
      router.push({ name: 'login' })
    } catch (error) {
      console.error('Error resetting password:', error)
      const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Failed to reset password. Please try again.'
      toastStore.showToastMessage(errorMessage, 'error', 'close')
    }
  }

  return {
    userId,
    isAdmin,
    login,
    registerUser,
    logout,
    forgotPassword,
    resetPassword,
    fetchCurrentUser
  }
})
